import { MediaResolver, ResolvedMedia, ResolverContext } from '../types';
import { isImageContentType, isVideoContentType } from '../utils/contentType';
import { extractJsonLdVideoUrl, extractMetaContent, toAbsoluteUrl } from '../utils/htmlMeta';

export class OpenGraphResolver implements MediaResolver {
  name = 'OpenGraphResolver';

  async canResolve(context: ResolverContext): Promise<boolean> {
    try {
      const content = await context.fetchContent(context.input.url);

      if (!content.html) {
        return isImageContentType(content.contentType) || isVideoContentType(content.contentType);
      }

      return Boolean(
        extractMetaContent(content.html, 'property', 'og:video:secure_url') ??
          extractMetaContent(content.html, 'property', 'og:video') ??
          extractMetaContent(content.html, 'property', 'og:video:url') ??
          extractMetaContent(content.html, 'name', 'twitter:player:stream') ??
          extractMetaContent(content.html, 'property', 'og:image') ??
          extractJsonLdVideoUrl(content.html)
      );
    } catch {
      return false;
    }
  }

  async resolve(context: ResolverContext): Promise<ResolvedMedia | null> {
    const content = await context.fetchContent(context.input.url);

    if (!content.html) {
      if (isVideoContentType(content.contentType)) {
        return {
          mediaType: 'video',
          playerType: 'html5',
          playbackUrl: content.finalUrl,
          thumbnailUrl: context.input.thumbnailUrl ?? undefined,
          title: context.input.title ?? undefined,
          resolved: true,
          resolverName: this.name,
        };
      }

      if (isImageContentType(content.contentType)) {
        return {
          mediaType: 'image',
          playerType: 'html5',
          playbackUrl: content.finalUrl,
          thumbnailUrl: content.finalUrl,
          title: context.input.title ?? undefined,
          resolved: true,
          resolverName: this.name,
        };
      }

      return null;
    }

    const videoUrl =
      extractMetaContent(content.html, 'property', 'og:video:secure_url') ??
      extractMetaContent(content.html, 'property', 'og:video:url') ??
      extractMetaContent(content.html, 'property', 'og:video') ??
      extractMetaContent(content.html, 'name', 'twitter:player:stream') ??
      extractJsonLdVideoUrl(content.html);

    if (videoUrl) {
      return {
        mediaType: 'video',
        playerType: 'html5',
        playbackUrl: toAbsoluteUrl(videoUrl, content.finalUrl),
        thumbnailUrl:
          extractMetaContent(content.html, 'property', 'og:image') ??
          context.input.thumbnailUrl ??
          undefined,
        title:
          extractMetaContent(content.html, 'property', 'og:title') ??
          context.input.title ??
          undefined,
        description: extractMetaContent(content.html, 'property', 'og:description') ?? undefined,
        resolved: true,
        resolverName: this.name,
      };
    }

    const imageUrl = extractMetaContent(content.html, 'property', 'og:image');

    if (imageUrl) {
      return {
        mediaType: 'image',
        playerType: 'html5',
        playbackUrl: toAbsoluteUrl(imageUrl, content.finalUrl),
        thumbnailUrl: toAbsoluteUrl(imageUrl, content.finalUrl),
        title:
          extractMetaContent(content.html, 'property', 'og:title') ??
          context.input.title ??
          undefined,
        description: extractMetaContent(content.html, 'property', 'og:description') ?? undefined,
        resolved: true,
        resolverName: this.name,
      };
    }

    return null;
  }
}
